import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ReactComponent as HassantukLogo } from "src/assets/icons/hassantuk-logo-header.svg";
import HorizontalDivider from "src/components/HorizontalDivider/HorizontalDivider";
import VerticalDivider from "src/components/VerticalDivider/VerticalDivider";
import {
  convertDateIFNotMin,
  getTextValueById,
  isRtl,
} from "src/utils/helpers";
import InvoiceNotes from "./InvoiceNotes";
import InvoiceCompanyDetails from "./InvoiceCompanyDetails";
import InvoicePropertyDetails from "./InvoicePropertyDetails";
import InvoiceTotalAmounts from "./ConsolidatedInvoice/InvoiceTotalAmounts";

interface InvoiceDetailsProps {
  contentRef: React.RefObject<HTMLDivElement>;
}

const InvoiceDetailsPrintView: React.FC<InvoiceDetailsProps> = ({
  contentRef,
}) => {
  const { t } = useTranslation(["common", "registration", "invoice"]);
  const invoiceStatusOptions = useSelector(
    (state: any) => state.lookup.invoiceStatusOptions
  );
  const invoiceDetails = useSelector(
    (state: any) => state.invoices?.invoiceDetailsPopupData?.invoiceDetails
  );
  const {
    invoiceNumber,
    totalAmountWithoutTax,
    totalAmountWithTax,
    invoiceLineItems,
    paymentDate,
    paymentDueDate,
    totalTax,
    paymentStatus,
  } = invoiceDetails;
  const { systems, language } = useSelector((state: any) => state.lookup);

  return (
    <div ref={contentRef}>
      <div
        className="print-view print-only"
        style={{ direction: isRtl(language) ? "rtl" : "ltr" }}
      >
        <div className="invoice-details flex-column">
          <section className="invoice-header">
            <div className="invoice-header__logos d-flex justify-content-between align-items-center mb-2">
              <HassantukLogo className="hassantuk" />
              <div className="invoice-header__details__title w-100 d-flex justify-content-center">
              <h6 className="text-center mt-3">
                  {t("invoiceDetails.invoiceTitle")}
                </h6>
              </div>
            </div>
            <div className="invoice-header__details">
              <HorizontalDivider />
              <div className="invoice-header__details__info d-flex flex-column">
                <div className="invoice-header__details__info__row d-flex">
                  <div className="invoice-header__details__info__row__item d-flex align-items-center justify-content-between pe-2 py-1 w-50">
                    <h6>{t("invoiceDetails.invoiceNumber")}</h6>
                    <span>{invoiceNumber}</span>
                  </div>
                  <VerticalDivider />
                  <div className="invoice-header__details__info__row__item d-flex align-items-center justify-content-between px-2 py-1 w-50">
                    <h6>{t("invoiceDetails.invoiceDueDate")}</h6>
                    <span className="plain-text-date">
                      {convertDateIFNotMin(paymentDueDate)}
                    </span>
                  </div>
                </div>
                <HorizontalDivider />

                <div className="invoice-header__details__info__row d-flex">
                  <div className="invoice-header__details__info__row__item d-flex align-items-center justify-content-between pe-2 py-1 w-50">
                    <h6>{t("invoiceStatus")}</h6>
                    <span>
                      {getTextValueById(
                        paymentStatus,
                        invoiceStatusOptions,
                        "id"
                      )}
                    </span>
                  </div>
                  <VerticalDivider />
                  <div className="invoice-header__details__info__row__item d-flex align-items-center justify-content-between px-2 py-1 w-50">
                    <h6>{t("invoiceDetails.paymentDate")}</h6>
                    <span className="plain-text-date">
                      <span> {convertDateIFNotMin(paymentDate)}</span>
                    </span>
                  </div>
                </div>
              </div>
              <HorizontalDivider />
            </div>
          </section>
          <InvoiceCompanyDetails />

          <InvoicePropertyDetails />

          <section className="invoice-body invoice-amount mt-1">
            <div className="invoice-body__sub-title py-1 px-2 d-flex justify-content-between align-items-start">
              <span className="w-70">{t("invoiceDetails.item")}</span>
              <span className="w-25 text-center">
                {t("invoiceDetails.feesAED")}
              </span>
            </div>
            <div className="invoice-header__details__info d-flex flex-column">
              {invoiceLineItems?.map((invoiceItem: any, index: number) => (
                <>
                  <div className="invoice-header__details__info__row d-flex justify-content-between px-2">
                    <div
                      className={`invoice-header__details__info__row__item w-80`}
                    >
                      <h6>
                        {" "}
                        {getTextValueById(
                          invoiceItem?.systemServiceId,
                          systems as unknown as [],
                          "systemServiceId"
                        )}
                      </h6>
                    </div>
                    <VerticalDivider />
                    <div className="invoice-header__details__info__row__item text-center w-25">
                      <span>{invoiceItem.totalAmount}</span>
                    </div>
                  </div>
                  <HorizontalDivider />
                </>
              ))}
              <InvoiceTotalAmounts
                totalAmount={totalAmountWithTax}
                totalAmountWithoutTax={totalAmountWithoutTax}
                totalTax={totalTax}
              />
            </div>
            <HorizontalDivider />
          </section>
          <InvoiceNotes />
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetailsPrintView;
